import { useQuery } from '@tanstack/react-query';
import type { UserRole } from 'scheduler-types';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface PersonSkill {
  id: string;
  name: string;
  description: string | null;
}

export interface PersonListItem {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  skills: PersonSkill[];
  createdAt: string;
  updatedAt: string;
}

export interface PersonDetail extends PersonListItem {
  lunchStartTime?: string | null;
  lunchDurationMinutes?: number | null;
  availability?: AvailabilityRule[];
}

export interface AvailabilityRule {
  id: string;
  personId: string;
  dayOfWeek: number; // 0=Sunday, 6=Saturday
  startTime: string; // "HH:MM"
  endTime: string;   // "HH:MM"
  effectiveFrom: string;
  effectiveUntil: string | null;
}

interface PeopleFilters {
  role?: UserRole;
  search?: string;
  isActive?: boolean;
}

async function fetchPeople(filters: PeopleFilters = {}): Promise<PersonListItem[]> {
  const params = new URLSearchParams();

  if (filters.role) {
    params.append('role', filters.role);
  }
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.isActive !== undefined) {
    params.append('isActive', String(filters.isActive));
  }

  const queryString = params.toString();
  const url = apiUrl(`/api/v1/people${queryString ? `?${queryString}` : ''}`);

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch people');
  }

  const json = await response.json();
  const data = json.data;

  // Defensive check - ensure we return an array
  if (!Array.isArray(data)) {
    console.error('[usePeople] Expected array but got:', typeof data, data);
    return [];
  }

  return data;
}

async function fetchPerson(id: string): Promise<PersonDetail> {
  const response = await fetch(apiUrl(`/api/v1/people/${id}`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch person');
  }

  const json = await response.json();
  return json.data;
}

export function usePeople(filters: PeopleFilters = {}) {
  return useQuery({
    queryKey: ['people', filters],
    queryFn: () => fetchPeople(filters),
    staleTime: 30000,
  });
}

export function usePerson(id: string | undefined) {
  return useQuery({
    queryKey: ['person', id],
    queryFn: () => fetchPerson(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}
